import React, { useContext } from "react";
import { Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../starek.css";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";

function OperationBasicDetails() {
    const [name, setName] = useSyncedField("name", "");
    const [description, setDescription] = useSyncedField("description", "");
    const [isProductive, setIsProductive] = useSyncedField("is_productive", true);

    return (
        <>
            <Form.Group className="mb-3" controlId="operation_name">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    maxLength={191}
                    name="operation_name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-3" controlId="operation_description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Optional, up to 191 characters"
                    maxLength={191}
                    name="operation_description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-3" controlId="operation_category">
                <Form.Label>
                    Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                    name="operation_category"
                    value={isProductive ? "productive" : "non_productive"}
                    onChange={(e) =>
                        setIsProductive(e.target.value === "productive")
                    }
                >
                    <option value="productive">Productive</option>
                    <option value="non_productive">Non-productive</option>
                </Form.Select>
                <Form.Text muted>
                    Productive operations add value to the part; non-productive
                    ones (setup, transfer, wait) do not.
                </Form.Text>
            </Form.Group>

        </>
    );
}

/**
 * An operation on its own does nothing: it needs a machine that lists it as a Capability
 * and a part that asks for it in its Route. Both live on other screens, so the panel
 * points at them instead of leaving the user to find the connection.
 */
const operationNextSteps = ({ data, entity, isNew }) => {
    const name = data.name?.trim() || entity?.name || "Operation";

    if (!isNew) {
        return {
            heading: `"${name}" updated`,
            allowAddAnother: false,
            steps: [
                {
                    label: "Check the machines that can run it",
                    why: "A machine only offers operations it lists under Capability.",
                    path: ["Manage", "Devices", "open a machine", "Capability"],
                    to: "/devices",
                },
                {
                    label: "Check the parts that use it",
                    why: "A part's Route decides where this operation actually runs.",
                    path: ["Setup", "Parts", "open a part", "Route"],
                    to: "/parts",
                },
            ],
        };
    }

    return {
        heading: `"${name}" created`,
        subheading:
            "Nothing runs it yet — a machine has to be able to do it, and a part has to ask for it.",
        allowAddAnother: true,
        steps: [
            {
                label: "Give a machine this capability",
                why: "Until one machine lists it, this operation cannot be scheduled.",
                path: ["Manage", "Devices", "open a machine", "Capability"],
                to: "/devices",
            },
            {
                label: "Add it to a part's route",
                why: "The Route is the ordered list of operations that build a part.",
                path: ["Setup", "Parts", "open a part", "Route"],
                to: "/parts",
            },
            {
                label: "Schedule a run",
                why: "Once a part's route includes it, book the run and track progress.",
                path: ["Plan", "Job Schedules", "Add"],
                to: "/job-schedules",
            },
        ],
    };
};

function OperationSidebar({
    show,
    item,
    plantId,
    onDiscard,
    onSave,
    onDelete,
    onToggleActive,
    onOpenSaved,
}) {
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            if (!emittedData.name?.trim()) {
                starekContext.showError("Operation name is required");
                throw new Error("Operation name is required");
            }

            const payload = {
                id: entity?.id,
                name: emittedData.name.trim(),
                description: emittedData.description || "",
                // Untouched on a new entity means the default: Productive.
                is_productive: emittedData.is_productive !== false,
                plant_id: plantId,
            };

            return await onSave(payload);
        } catch (error) {
            console.error("Error saving operation:", error);
            starekContext.showError(
                error?.message || "Failed to save operation. Please try again.",
            );
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? async (entity) => await onDelete(entity) : null}
            onDiscard={async () => await onDiscard()}
            onToggleActive={onToggleActive}
            nextSteps={operationNextSteps}
            onOpenSaved={onOpenSaved}
        >
            <SidebarView entityName="Operation" entityCode="operation">
                <SidebarView.Page
                    key={item?.id || "new"}
                    name="basic_details"
                    title="Basic Details"
                >
                    <OperationBasicDetails />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default OperationSidebar;
